import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  BooleanInput,
  Create,
  Edit,
  FilterForm,
  List,
  required,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  TextInput,
  useInput,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  usePermissions,
  Form,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PermissionButton } from 'src/routes/components';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';
import { Resources } from '../../resource';
import { SubmitDialog } from '../records/RecordsPanel';
import {
  NotificationTemplateTranslation,
  categoryAncestors,
  deleteNotificationTemplate,
  listAllNotificationCategories,
  sortCategoryTree,
  updateNotificationTemplate,
} from '../../../services/notificationConfigsService';

const RESOURCE = Resources.notificationConfigsTemplates;

/** Casbin resource guarding every /notification-configs write. */
const PERMISSION_RESOURCE = 'notification-configs';

const LANGUAGE_CODES = [
  'vi',
  'en',
  'es',
  'hi',
  'zh',
  'zh-Hant',
  'ja',
  'pt',
  'fr',
  'de',
  'it',
  'tr',
  'ko',
  'ar',
];

const LANGUAGE_CHOICES = LANGUAGE_CODES.map((id) => ({ id, name: id }));

const IS_ACTIVE_CHOICES = [
  { id: true, name: 'common.active' },
  { id: false, name: 'common.inactive' },
];

const TARGET_AUDIENCE_CHOICES = [
  { id: 'SPECIFIED_UID', name: 'SPECIFIED_UID' },
  { id: 'ALL_USER', name: 'ALL_USER' },
];

const TARGET_AUDIENCE_COLOR: Record<string, 'secondary' | 'primary'> = {
  SPECIFIED_UID: 'secondary',
  ALL_USER: 'primary',
};

const CHANNEL_TYPE_CHOICES = [
  { id: 'PUSH', name: 'PUSH' },
  // { id: 'EMAIL', name: 'EMAIL' },
  // { id: 'SMS', name: 'SMS' },
];

const PLATFORM_CHOICES = [
  { id: 'IOS', name: 'IOS' },
  { id: 'ANDROID', name: 'ANDROID' },
  { id: 'WEB', name: 'WEB' },
];

const TemplateListActions = ({
  onCreate,
  onPush,
}: {
  onCreate: () => void;
  onPush: () => void;
}) => {
  const t = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const hasFilters = Object.keys(filterValues ?? {}).length > 0;

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        gap: 1.5,
        alignItems: 'start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        width: '100%',
        '& form': {
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          flex: 1,
          minHeight: '40px',
        },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          alignItems: 'start',
        }}
      >
        <FilterForm
          filters={[
            <TextInput
              key="templateCode"
              source="templateCode"
              label="module.notificationConfigs.templateCode"
              alwaysOn
              resettable
              size="small"
            />,
            // <SelectInput
            //   key="languageCode"
            //   source="languageCode"
            //   label="module.notificationConfigs.languageCode"
            //   choices={LANGUAGE_CHOICES}
            //   alwaysOn
            //   resettable
            //   size="small"
            //   emptyText="actions.all"
            //   sx={{ minWidth: 130 }}
            // />,
            <SelectInput
              key="targetAudience"
              source="targetAudience"
              label="module.notificationConfigs.targetAudience"
              choices={TARGET_AUDIENCE_CHOICES}
              alwaysOn
              resettable
              size="small"
              emptyText="actions.all"
              sx={{ minWidth: 170 }}
            />,
            // <SelectInput
            //   key="platforms"
            //   source="platforms"
            //   label="module.notificationConfigs.platform"
            //   choices={PLATFORM_CHOICES}
            //   alwaysOn
            //   resettable
            //   size="small"
            //   emptyText="actions.all"
            //   sx={{ minWidth: 130 }}
            // />,
            // <SelectArrayInput
            //   key="platforms"
            //   source="platforms"
            //   label="module.notificationConfigs.platforms"
            //   choices={PLATFORM_CHOICES}
            //   alwaysOn
            //   size="small"
            //   sx={{ minWidth: 215 }}
            // />,
            // <SelectInput
            //   key="isActive"
            //   source="isActive"
            //   label="module.notificationConfigs.isActive"
            //   choices={IS_ACTIVE_CHOICES}
            //   alwaysOn
            //   resettable
            //   size="small"
            //   emptyText="actions.all"
            //   sx={{ minWidth: 120 }}
            // />,
          ]}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={!hasFilters}
          onClick={() => setFilters({}, [])}
          sx={{ minHeight: 40 }}
        >
          {t('common.reset', { _: 'Reset' })}
        </Button>
      </Box>
      <PermissionButton resource={PERMISSION_RESOURCE} action="create">
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={onCreate}
          sx={{ minHeight: 40 }}
        >
          {t('module.notificationConfigs.createTemplate', { _: 'Create Template' })}
        </Button>
      </PermissionButton>
      {/* <Button
        variant="contained"
        size="small"
        color="secondary"
        onClick={onPush}
        sx={{ minHeight: 40 }}
      >
        {t('module.notificationConfigs.pushNewNotification', { _: 'Push new notification' })}
      </Button> */}
    </Box>
  );
};

// ── Category Code Select ──────────────────────────────────────────────────────

const CategoryCodeSelectInput = () => {
  const t = useTranslate();
  const [choices, setChoices] = useState<{ id: string; name: string }[]>([]);

  // A template takes one category at any level; the path tells a sub-category from its parent.
  useEffect(() => {
    listAllNotificationCategories()
      .then((items) => {
        const ancestors = categoryAncestors(items);
        setChoices(
          sortCategoryTree(items).map((c) => ({
            id: c.categoryCode,
            name: [...(ancestors.get(c.id) ?? []), c].map((a) => a.categoryCode).join(' › '),
          }))
        );
      })
      .catch(() => {});
  }, []);

  return (
    <SelectInput
      source="notificationTypeCategoryCode"
      label={t('module.notificationConfigs.notificationTypeCategoryCode')}
      choices={choices}
      fullWidth
      size="small"
      emptyText="—"
    />
  );
};

// ── Shared form ───────────────────────────────────────────────────────────────

const TemplateForm = ({ mode }: { mode: 'create' | 'edit' }) => {
  const t = useTranslate();
  return (
    <Stack spacing={1} sx={{ pt: 1, pb: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextInput
          source="templateCode"
          label={t('module.notificationConfigs.templateCode')}
          parse={(value) => (typeof value === 'string' ? value.replace(/\s+/g, '') : value)}
          validate={[required()]}
          // readOnly, not disabled: disabled fields are stripped from the submitted values.
          readOnly={mode === 'edit'}
          fullWidth
          size="small"
        />
        <SelectInput
          source="channelType"
          label={t('module.notificationConfigs.channelType')}
          choices={CHANNEL_TYPE_CHOICES}
          defaultValue="PUSH"
          validate={[required()]}
          fullWidth
          size="small"
        />
        <CategoryCodeSelectInput />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <SelectInput
          source="targetAudience"
          label={t('module.notificationConfigs.targetAudience')}
          choices={TARGET_AUDIENCE_CHOICES}
          defaultValue="SPECIFIED_UID"
          validate={[required()]}
          fullWidth
          size="small"
        />
        <SelectArrayInput
          source="platforms"
          label={t('module.notificationConfigs.platforms')}
          choices={PLATFORM_CHOICES}
          defaultValue={['IOS', 'ANDROID', 'WEB']}
          sx={{ minWidth: 240 }}
          size="small"
        />
        <BooleanInput
          source="isActive"
          label={t('module.notificationConfigs.isActive')}
          defaultValue
        />
      </Stack>
      <TextInput
        source="description"
        label={t('module.notificationConfigs.description')}
        fullWidth
        size="small"
      />

      <Typography variant="subtitle2" color="text.secondary">
        {t('module.notificationConfigs.translations', { _: 'Translations' })}
      </Typography>
      <TranslationsInput />
    </Stack>
  );
};

// ── Create popup ─────────────────────────────────────────────────────────────

interface PopupProps {
  onClose: () => void;
}

const CreatePopup = ({ onClose }: PopupProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  return (
    <Create
      resource={RESOURCE}
      redirect={false}
      mutationOptions={{
        onSuccess: () => {
          notify(t('module.notificationConfigs.createSuccess', { _: 'Template created' }), {
            type: 'success',
            autoHideDuration: 1500,
          });
          onClose();
          refresh();
        },
        onError: (err: any) => {
          notify(
            err?.message ?? t('module.notificationConfigs.createFailed', { _: 'Create failed' }),
            { type: 'error' }
          );
        },
      }}
      sx={{ boxShadow: 'none', '.MuiCard-root': { boxShadow: 'none' } }}
    >
      {/* Start with an empty row per fixed language so the grid is ready to fill in. */}
      <Form defaultValues={{ translations: seedTranslations() }}>
        <DialogTitle>
          {t('module.notificationConfigs.createTemplate', { _: 'Create Template' })}
        </DialogTitle>
        <DialogContent>
          <TemplateForm mode="create" />
        </DialogContent>
        <DialogActions>
          <MuiButton variant="outlined" onClick={onClose}>
            {t('actions.cancel')}
          </MuiButton>
          <PermissionButton resource={PERMISSION_RESOURCE} action="create">
            <SaveButton />
          </PermissionButton>
        </DialogActions>
      </Form>
    </Create>
  );
};

// ── Edit popup ───────────────────────────────────────────────────────────────

interface EditPopupProps extends PopupProps {
  id: string | number;
}

const EditPopup = ({ id, onClose }: EditPopupProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  return (
    <Edit
      resource={RESOURCE}
      id={id}
      redirect={false}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify(t('module.notificationConfigs.updateSuccess', { _: 'Template updated' }), {
            type: 'success',
            autoHideDuration: 1500,
          });
          onClose();
          refresh();
        },
        onError: (err: any) => {
          notify(
            err?.message ?? t('module.notificationConfigs.updateFailed', { _: 'Update failed' }),
            { type: 'error' }
          );
        },
      }}
      sx={{ boxShadow: 'none', '.MuiCard-root': { boxShadow: 'none' } }}
    >
      <Form>
        <DialogTitle>
          {t('module.notificationConfigs.editTemplate', { _: 'Edit Template' })}
        </DialogTitle>
        <DialogContent>
          <TemplateForm mode="edit" />
        </DialogContent>
        <DialogActions>
          <MuiButton variant="outlined" onClick={onClose}>
            {t('actions.cancel')}
          </MuiButton>
          <PermissionButton resource={PERMISSION_RESOURCE} action="update">
            <SaveButton />
          </PermissionButton>
        </DialogActions>
      </Form>
    </Edit>
  );
};

// ── Translations ─────────────────────────────────────────────────────────────

type TranslationDraft = {
  id?: string;
  languageCode: string;
  subjectTemplate: string;
  bodyTemplate: string;
  isEnabled: boolean;
};

const emptyTranslation = (languageCode: string): TranslationDraft => ({
  languageCode,
  subjectTemplate: '',
  bodyTemplate: '',
  isEnabled: true,
});

/** All fixed languages seeded from the record, followed by any custom-language rows. */
const seedTranslations = (
  source?: NotificationTemplateTranslation[] | null
): TranslationDraft[] => {
  const fixed = LANGUAGE_CODES.map((languageCode) => {
    const existing = source?.find((tr) => tr.languageCode === languageCode);
    return existing
      ? { ...existing, isEnabled: existing.isEnabled ?? true }
      : emptyTranslation(languageCode);
  });
  const custom = (source ?? [])
    .filter((tr) => !LANGUAGE_CODES.includes(tr.languageCode))
    .map((tr) => ({ ...tr, isEnabled: tr.isEnabled ?? true }));
  return [...fixed, ...custom];
};

const isCustomTranslation = (index: number) => index >= LANGUAGE_CODES.length;

/** Editable translations grid, shared by the list expand panel and the create/edit dialog. */
const TranslationsTable = ({
  translations,
  onChange,
  deletingId,
  onDeleteSaved,
}: {
  translations: TranslationDraft[];
  onChange: (next: TranslationDraft[]) => void;
  deletingId?: string | null;
  onDeleteSaved?: (translation: TranslationDraft) => void;
}) => {
  const t = useTranslate();
  const { data } = usePermissions<PermissionData>();
  const { permissions = [] } = data || {};
  // Only the saved-translation branch calls DELETE /notification-configs/templates; local draft
  // rows are removed client-side and stay available to read-only users inside the form.
  const canDeleteSaved = hasPermission(permissions, PERMISSION_RESOURCE, 'delete');
  console.log('TranslationsTable render', translations);

  const setField = (index: number, field: keyof TranslationDraft, value: any) =>
    onChange(translations.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr)));

  const addCustomTranslation = () => onChange([...translations, emptyTranslation('')]);

  const removeTranslation = (index: number) => onChange(translations.filter((_, i) => i !== index));

  const handleDelete = (translation: TranslationDraft, index: number) => {
    // Saved translations are removed server-side when the caller supports it; drafts are local only.
    if (translation.id && onDeleteSaved) onDeleteSaved(translation);
    else removeTranslation(index);
  };

  // Spreadsheet-style paste: a tab/newline-delimited block (e.g. copied from Excel) fills down
  // and across from the focused cell.
  const handlePaste = (
    startIndex: number,
    startField: 'subjectTemplate' | 'bodyTemplate',
    e: React.ClipboardEvent
  ) => {
    const text = e.clipboardData.getData('text');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;
    e.preventDefault();

    const grid = text
      .replace(/\r/g, '')
      .replace(/\n+$/, '')
      .split('\n')
      .map((line) => line.split('\t'));

    const fields = ['subjectTemplate', 'bodyTemplate'] as const;
    const startCol = (fields as readonly string[]).indexOf(startField);

    const next = translations.map((tr) => ({ ...tr }));
    grid.forEach((cells, r) => {
      const target = next[startIndex + r];
      if (!target) return;
      cells.forEach((val, c) => {
        const field = fields[startCol + c];
        if (field) target[field] = val;
      });
    });
    onChange(next);
  };

  return (
    <>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>
                {t('module.notificationConfigs.languageCode', { _: 'Language' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 220 }}>
                {t('module.notificationConfigs.subjectTemplate', { _: 'Subject' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 280 }}>
                {t('module.notificationConfigs.bodyTemplate', { _: 'Body' })}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>
                {t('module.notificationConfigs.isEnabled', { _: 'Enabled' })}
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {translations.map((tr, index) => {
              const custom = isCustomTranslation(index);
              return (
                <TableRow key={custom ? `custom-${index}` : tr.languageCode}>
                  <TableCell
                    sx={{ fontFamily: 'monospace', verticalAlign: 'top', pt: custom ? 0.8 : 2.5 }}
                  >
                    {custom ? (
                      <TextField
                        value={tr.languageCode}
                        onChange={(e) => setField(index, 'languageCode', e.target.value.trim())}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder={t('module.notificationConfigs.languageCode', {
                          _: 'Language',
                        })}
                      />
                    ) : (
                      tr.languageCode
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      value={tr.subjectTemplate}
                      onChange={(e) => setField(index, 'subjectTemplate', e.target.value)}
                      onPaste={(e) => handlePaste(index, 'subjectTemplate', e)}
                      size="small"
                      fullWidth
                      multiline
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      value={tr.bodyTemplate}
                      onChange={(e) => setField(index, 'bodyTemplate', e.target.value)}
                      onPaste={(e) => handlePaste(index, 'bodyTemplate', e)}
                      size="small"
                      fullWidth
                      multiline
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                    <Switch
                      checked={tr.isEnabled}
                      onChange={(e) => setField(index, 'isEnabled', e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {(custom || tr.id) && (!(tr.id && onDeleteSaved) || canDeleteSaved) && (
                      <Tooltip title={t('actions.delete')} arrow>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={Boolean(tr.id) && deletingId === tr.id}
                            onClick={() => handleDelete(tr, index)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addCustomTranslation}>
          {t('module.notificationConfigs.addTranslation', { _: 'Add language' })}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {t('module.notificationConfigs.pasteFromExcelHint', {
            _: 'Tip: paste from Excel into a cell to fill multiple rows',
          })}
        </Typography>
      </Stack>
    </>
  );
};

/** Binds the translations grid to the surrounding react-admin form. */
const TranslationsInput = () => {
  const { field } = useInput({ source: 'translations', defaultValue: [] });
  const record = useRecordContext<any>();
  const recordId = record?.id ?? '__create__';
  // Local display state keeps the LANGUAGE_CODES order regardless of react-admin re-syncing the
  // form field from the (unsorted) record; every change is mirrored back to the form.
  const [rows, setRows] = useState<TranslationDraft[]>(() =>
    seedTranslations(record?.translations ?? field.value)
  );
  const seededRef = useRef<string | number>('');

  useEffect(() => {
    if (seededRef.current === recordId) return;
    const seeded = seedTranslations(record?.translations ?? field.value);
    setRows(seeded);
    field.onChange(seeded);
    seededRef.current = recordId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, record]);

  const handleChange = (next: TranslationDraft[]) => {
    setRows(next);
    field.onChange(next);
  };

  return <TranslationsTable translations={rows} onChange={handleChange} />;
};

const TranslationsPanel = ({ record }: { record: any }) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [translations, setTranslations] = useState<TranslationDraft[]>(() =>
    seedTranslations(record?.translations)
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Re-seed when the record changes (e.g. after a refresh) so edits reflect fresh server data.
  useEffect(() => {
    setTranslations(seedTranslations(record?.translations));
  }, [record?.translations]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // The API replaces the whole template, so resend its shared attributes unchanged.
      await updateNotificationTemplate({
        templateCode: record.templateCode,
        channelType: record.channelType,
        notificationTypeCategoryCode: record.notificationTypeCategoryCode,
        targetAudience: record.targetAudience,
        oneSignalTemplateID: record.oneSignalTemplateID,
        description: record.description,
        platforms: record.platforms,
        isActive: record.isActive,
        translations,
      });
      notify(t('module.notificationConfigs.updateSuccess', { _: 'Template updated' }), {
        type: 'success',
        autoHideDuration: 1500,
      });
      refresh();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSaved = async (translation: TranslationDraft) => {
    setDeleting(translation.id!);
    try {
      await deleteNotificationTemplate(
        record.templateCode,
        translation.languageCode,
        record.channelType
      );
      notify(t('module.notificationConfigs.deleteSuccess', { _: 'Translation deleted' }), {
        type: 'success',
        autoHideDuration: 1500,
      });
      refresh();
    } catch (err: any) {
      notify(err?.message ?? t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Box sx={{ m: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          {t('module.notificationConfigs.translations', { _: 'Translations' })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setTranslations(seedTranslations(record?.translations))}
            disabled={saving}
          >
            {t('common.reset', { _: 'Reset' })}
          </Button>
          <PermissionButton resource={PERMISSION_RESOURCE} action="update">
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
              {t('actions.save', { _: 'Save' })}
            </Button>
          </PermissionButton>
        </Stack>
      </Stack>

      <TranslationsTable
        translations={translations}
        onChange={setTranslations}
        deletingId={deleting}
        onDeleteSaved={handleDeleteSaved}
      />
    </Box>
  );
};

// ── List table ───────────────────────────────────────────────────────────────

const TemplateRow = ({
  row,
  open,
  onToggle,
  onEdit,
}: {
  row: any;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) => {
  const t = useTranslate();

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={onToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}>{row.templateCode}</TableCell>
        <TableCell>{row.description ? row.description : '-'}</TableCell>
        <TableCell>{row.channelType}</TableCell>
        <TableCell>{row.notificationTypeCategoryCode ?? '-'}</TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          {row.targetAudience ? (
            <Chip
              label={row.targetAudience}
              size="small"
              variant="outlined"
              color={TARGET_AUDIENCE_COLOR[row.targetAudience]}
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.platforms?.length
              ? row.platforms.map((p: string) => <Chip key={p} label={p} size="small" />)
              : '-'}
          </Stack>
        </TableCell>
        <TableCell>
          <Chip
            label={row.isActive ? 'ON' : 'OFF'}
            size="small"
            color={row.isActive ? 'success' : 'default'}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title={row.updatedAt ?? ''} arrow>
            <span>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" onClick={onEdit}>
              {t('actions.edit')}
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TranslationsPanel record={row} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TemplatesTable = ({ onEdit }: { onEdit: (record: any) => void }) => {
  const t = useTranslate();
  const { data, isPending } = useListContext();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const items: any[] = data ?? [];

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }} />
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.templateCode', { _: 'Template Code' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.description', { _: 'Description' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.channelType', { _: 'Channel' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.notificationTypeCategoryCode', { _: 'Category Code' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.targetAudience', { _: 'Target Audience' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.platforms', { _: 'Platforms' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.isActive', { _: 'Active' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.notificationConfigs.updatedAt', { _: 'Updated At' })}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('common.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isPending ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary" variant="body2">
                  {t('common.loading', { _: 'Loading...' })}
                </Typography>
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary" variant="body2">
                  {t('ra.navigation.no_results')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((row) => (
              <TemplateRow
                key={row.id}
                row={row}
                open={expandedId === row.id}
                onToggle={() => setExpandedId((prev) => (prev === row.id ? null : row.id))}
                onEdit={() => onEdit(row)}
              />
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

// ── Panel ────────────────────────────────────────────────────────────────────

export const TemplatesPanel = () => {
  const t = useTranslate();
  const [popup, setPopup] = useState<ReactElement | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);

  return (
    <>
      <Card sx={{ mt: 2 }}>
        <List
          resource={RESOURCE}
          actions={
            <TemplateListActions
              onCreate={() => setPopup(<CreatePopup onClose={() => setPopup(null)} />)}
              onPush={() => setSubmitOpen(true)}
            />
          }
        >
          <Card
            sx={{
              boxShadow: 'none',
            }}
          >
            <TemplatesTable
              onEdit={(record) =>
                setPopup(<EditPopup id={record.id} onClose={() => setPopup(null)} />)
              }
            />
          </Card>
        </List>
      </Card>

      <Dialog open={Boolean(popup)} onClose={() => setPopup(null)} maxWidth="md" fullWidth>
        {popup}
      </Dialog>

      <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="md" fullWidth>
        <SubmitDialog onClose={() => setSubmitOpen(false)} />
      </Dialog>
    </>
  );
};
